import TechnoArtLogo from "../components/technoArtLogo.tsx";
import React, { useEffect, useRef, useState } from "react";
import Background from "../components/background.tsx";
import Header from "../components/header.tsx";
import Sidebar from "../components/sidebar.tsx";
import ProfileCard from "../components/users/profile-card.tsx";
import UserListCard from "../components/users/user-list-card.tsx";

interface UserRecord {
  id: string;
  name: string;
  email: string;
  avatar: string;
  position: "Super Admin" | "Admin" | "Operator";
  location: string;
  status: "Online" | "Offline";
  lastLogin: string;
  assignedBy: string;
  createdDate: string;
}

const UserManagementPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const recordsPerPage = 5;

  const baseUsers: UserRecord[] = [
    {
      id: "1",
      name: "Galaxy Admin",
      email: "galaxyadmin@gmail.com",
      avatar: "https://thispersondoesnotexist.com/",
      position: "Super Admin",
      location: "-",
      status: "Online",
      lastLogin: "18/05/2025, 08:05:11",
      assignedBy: "-",
      createdDate: "15/05/2025, 11:08:45",
    },
    {
      id: "2",
      name: "Jane Doe",
      email: "janedoe@gmail.com",
      avatar: "https://thispersondoesnotexist.com/",
      position: "Admin",
      location: "Parking Park 1",
      status: "Online",
      lastLogin: "18/05/2025, 08:11:21",
      assignedBy: "John Doe",
      createdDate: "16/05/2025, 10:15:59",
    },
    {
      id: "3",
      name: "Darwin",
      email: "darwingamers@gmail.com",
      avatar: "https://thispersondoesnotexist.com/",
      position: "Admin",
      location: "Parking Park 2",
      status: "Offline",
      lastLogin: "18/05/2025, 08:00:01",
      assignedBy: "John Doe",
      createdDate: "16/05/2025, 10:16:17",
    },
    {
      id: "4",
      name: "Fauzan",
      email: "Fjaazann@gmail.com",
      avatar: "https://thispersondoesnotexist.com/",
      position: "Operator",
      location: "Parking Park 1",
      status: "Online",
      lastLogin: "18/05/2025, 07:59:55",
      assignedBy: "Jane Doe",
      createdDate: "17/05/2025, 08:16:20",
    },
    {
      id: "5",
      name: "Maria",
      email: "mmriaaa@gmail.com",
      avatar: "https://thispersondoesnotexist.com/",
      position: "Operator",
      location: "Parking Park 2",
      status: "Offline",
      lastLogin: "19/05/2025, 08:30:25",
      assignedBy: "Darwin",
      createdDate: "17/05/2025, 09:20:21",
    },
    {
      id: "6",
      name: "Cindy",
      email: "cindy123@gmail.com",
      avatar: "https://thispersondoesnotexist.com/",
      position: "Operator",
      location: "Parking Park 2",
      status: "Online",
      lastLogin: "20/05/2025, 08:28:05",
      assignedBy: "Darwin",
      createdDate: "18/05/2025, 08:12:01",
    },
  ];

  // Generate more dummy users using Array
  const moreUsers: UserRecord[] = Array.from({ length: 15 }, (_, i) => ({
    id: (i + 7).toString(),
    name: `User ${i + 7}`,
    email: `user${i + 7}@example.com`,
    avatar: "https://thispersondoesnotexist.com/",
    position: "Operator",
    location: `Parking Park ${(i % 2) + 1}`,
    status: i % 2 === 0 ? "Online" : "Offline",
    lastLogin: `20/05/2025, 08:${(10 + i).toString().padStart(2, "0")}:00`,
    assignedBy: ["Jane Doe", "Darwin"][i % 2],
    createdDate: `17/05/2025, 09:${(20 + i).toString().padStart(2, "0")}:21`,
  }));

  const userRecords: UserRecord[] = [...baseUsers, ...moreUsers];
  const totalEntries: number = userRecords.length;
  const totalPages = Math.ceil(totalEntries / recordsPerPage);

  const positionOrder = {
    "Super Admin": 1,
    Admin: 2,
    Operator: 3,
  };

  // dummy for now, get from auth context later
  const currentUserPosition = "Super Admin" as
    | "Super Admin"
    | "Admin"
    | "Operator";

  // Sort users by position order before filtering
  const sortedUserRecords: UserRecord[] = [...userRecords].sort(
    (a, b) => positionOrder[a.position] - positionOrder[b.position]
  );

  // Filter by search (username or email)
  const filteredUsers = sortedUserRecords.filter(
    (user) =>
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
  );

  // Filter by current user position: if Admin, only show Operators
  const filteredByPosition =
    currentUserPosition === "Admin"
      ? filteredUsers.filter((user) => user.position === "Operator")
      : filteredUsers;

  const totalFilteredEntries = filteredByPosition.length;
  const totalFilteredPages = Math.ceil(totalFilteredEntries / recordsPerPage);

  // Slice the records for the current page
  const pagedUsers = filteredByPosition.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Pagination logic like in history page
  const getPageNumbers = () => {
    if (totalFilteredPages <= 3) {
      return Array.from({ length: totalFilteredPages }, (_, i) => i + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    if (currentPage >= totalFilteredPages - 1) {
      return [
        totalFilteredPages - 2,
        totalFilteredPages - 1,
        totalFilteredPages,
      ];
    }
    return [currentPage - 1, currentPage, currentPage + 1];
  };
  const pageNumbers = getPageNumbers();

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalFilteredPages);
  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goToNextPage = () => {
    if (currentPage < totalFilteredPages) setCurrentPage(currentPage + 1);
  };

  const getPositionColor = (position: string) => {
    switch (position) {
      case "Super Admin":
        return "bg-purple-500 text-white whitespace-nowrap";
      case "Admin":
        return "bg-green-700 text-white whitespace-nowrap";
      case "Operator":
        return "bg-cyan-500 text-white whitespace-nowrap";
      default:
        return "bg-gray-500 text-white whitespace-nowrap";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Online":
        return "bg-green-500 text-white";
      case "Offline":
        return "bg-gray-400 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  // Dummy current user info (replace with real user data in production)
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profilePic, setProfilePic] = useState(
    "https://thispersondoesnotexist.com/"
  );
  const [profileName, setProfileName] = useState("Galaxy Admin");
  const [profileEmail] = useState("galaxyadmin@gmail.com");
  const [profilePosition] = useState(currentUserPosition);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle profile picture upload
  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) setProfilePic(ev.target.result as string);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // Handle save profile
  const handleSaveProfile = () => {
    setProfileEditMode(false);
    // Save logic here (API call, etc)
  };

  // Close menu when clicking outside
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".user-action-menu")) {
        setMenuOpenId(null);
      }
    };
    if (menuOpenId) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpenId]);

  // Helper for menu direction
  const shouldOpenUpward = (rowIndex: number) => {
    // Show upward if in the last 2 rows of the current page
    return rowIndex >= pagedUsers.length - 2;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Background />

      <div className="flex relative z-10">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <Header />

          {/* Content Area */}
          <div className="p-8">
            <div className="max-w-7xl mx-auto">
              {/* Edit User Profile Card */}
              <ProfileCard
                profilePic={profilePic}
                profileEditMode={profileEditMode}
                profileName={profileName}
                setProfileName={setProfileName}
                profilePosition={profilePosition}
                profileEmail={profileEmail}
                getPositionColor={getPositionColor}
                setProfileEditMode={setProfileEditMode}
                handleSaveProfile={handleSaveProfile}
                handleProfilePicChange={handleProfilePicChange}
              />

              {/* User List Card (handles Add/Edit/Delete modals internally) */}
              {currentUserPosition !== "Operator" && (
                <UserListCard
                  currentUserPosition={currentUserPosition}
                  search={search}
                  setSearch={setSearch}
                  setCurrentPage={setCurrentPage}
                  pagedUsers={pagedUsers}
                  getPositionColor={getPositionColor}
                  getStatusColor={getStatusColor}
                  menuOpenId={menuOpenId}
                  setMenuOpenId={setMenuOpenId}
                  shouldOpenUpward={shouldOpenUpward}
                  totalFilteredEntries={totalFilteredEntries}
                  currentPage={currentPage}
                  totalFilteredPages={totalFilteredPages}
                  pageNumbers={pageNumbers}
                  goToFirstPage={goToFirstPage}
                  goToLastPage={goToLastPage}
                  goToPreviousPage={goToPreviousPage}
                  goToNextPage={goToNextPage}
                  recordsPerPage={recordsPerPage}
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-4">
            <p className="text-gray-500 text-opacity-80 text-sm">
              &copy;2025, Galaxy Dev
            </p>
          </div>
        </div>
      </div>
      <TechnoArtLogo />
    </div>
  );
};

export default UserManagementPage;
